import omnitureHelper from 'utils/omnitureHelper';
import get from 'lodash/get';

import { getDefaultCartData, lookupChildItem } from 'utils/cartUtils';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CartItemState } from '@rome/response/cart/enums/CartItemState';

// eslint-disable-next-line import/no-extraneous-dependencies
import { CartView } from '@rome/request/cart/v5/CartView';

const pageName = 'View Cart';
const pageType = 'Cart';

const basketPageName = 'GROCERY:BasketView';
const basketPageType = 'Basket';

// TODO: Use enums from rome-models post APEX changes
const BUYBACK_ITEM_STATES = ['EXCHANGE_NOT_AVAILABLE', 'BUYBACK_TIER_CHANGED', 'BUYBACK_EXPIRED'];

const urgencyEvents = {
	no_sla_urgency: 'event294',
	sla_urgency_msg: 'event294,event296',
	sla_urgency_ticker: 'event294,event295'
};

export function additionalChildItemTracking(item, isChildItem) {
	if (!item || !isChildItem) return '';

	// This should happen only for child items. Since we reuse this function
	// for both parent and the child item, we're relying on the assumption that
	// a parent item won't have `childItemType` set to the value we're
	// checking. itemState is the key in checkout, but cartItemState is the one
	// in cart page :| The function we have in cartUtils for getting the
	// itemState uses `checkoutItemState`.
	const itemState = item.cartItemState || item.itemState;
	let event265 = false;
	let event266 = false;
	let prop74 = '';
	if (itemState === CartItemState.EXCHANGE_NOT_SERVICEABLE) {
		event265 = true;
	} else {
		event266 = BUYBACK_ITEM_STATES.indexOf(itemState) >= 0;
		prop74 = event266 ? itemState : '';
	}

	return `|event266=${event266}|event265=${event265}|prop74=${prop74}`;
}

// The isChild parameter denotes whether this toString routine is being used for any child items
function getItemTrackString(item, isBasketItem = false, isChildItem = false) {
	const { category, productId, vertical } = item;
	let productString = `${category};${productId};;;;eVar22=${category}|eVar19=${vertical}`;
	if (isBasketItem) {
		productString += `|eVar93=GROCERY`;
	}

	if (isChildItem) {
		productString += additionalChildItemTracking(item, isChildItem);

		// Exchange childItem related tracking.
		if (item.childItemType === 'REVERSE') {
			productString += '|eVar151=NOEX';
		}
	}

	const childItems = item.childItems;

	if (childItems && childItems.length) {
		const childString = childItems.map(item => getItemTrackString(item, isBasketItem, true)).join(',');
		productString = `${productString},${childString}`;
	}

	return productString;
}

function getServiceableVars(cartData) {
	const buyableStateItems = get(cartData, ['buyableStateItems']) || [];
	const buyableItemsCount = buyableStateItems.length;
	const nonServiceableItemsCount = buyableStateItems.filter(item => !item.serviceable).length;
	const serviceableItemsCount = buyableItemsCount - nonServiceableItemsCount;
	const FAProducts = buyableStateItems.filter(item => item.flipkartAssured === true);
	const FAProductsValue = FAProducts.reduce(
		(total, item) => (get(item, ['pricingInfo', 'finalPrice']) || 0) + total,
		0
	);
	const FAProductsShippingCharge = FAProducts.reduce(
		(total, item) => (get(item, ['pricingInfo', 'shippingCharge']) || 0) + total,
		0
	);
	let serviceableStatus;

	if (serviceableItemsCount === buyableItemsCount) {
		serviceableStatus = 'serviceable';
	} else if (nonServiceableItemsCount === buyableItemsCount) {
		serviceableStatus = 'non_serviceable';
	} else {
		serviceableStatus = 'partial_serviceable';
	}
	return {
		prop58: serviceableStatus,
		event109: serviceableItemsCount,
		event113: nonServiceableItemsCount,
		event95: FAProducts.length,
		event96: FAProductsValue,
		event91: FAProductsShippingCharge
	};
}

export function trackPageView(cartData, userState, location, isBasketItem = false) {
	const omniFields = {};
	const omnievents = ['scview'];
	const omniContext = {};
	let cartView = isBasketItem ? CartView.GROCERY : CartView.FLIPKART;
	const defaultCartData = getDefaultCartData(cartData, cartView);
	omniContext.pageName = pageName;
	omniContext.pageType = pageType;
	if (isBasketItem) {
		omniContext.pageName = basketPageName;
		omniContext.pageType = basketPageType;
	}

	let childItem = [];
	const buyableStateItems = get(defaultCartData, ['buyableStateItems']) || [];
	const coldStateItems = get(defaultCartData, ['coldStateItems']) || [];
	buyableStateItems.map(buyable => {
		buyable && buyable.nbfcOffers && (omniFields.eVar29 = 'BAJAJ_not_applicable_call');
		buyable &&
			buyable.childItems &&
			buyable.childItems.map(child => {
				childItem.push(child);
			});
		const slaTrackingEvent = get(buyable, ['trackingInfo', 'slaUrgency']);
		// Adding events for 11x11 & SLA callouts
		if (slaTrackingEvent) {
			omnievents.push(urgencyEvents[slaTrackingEvent]);
		}
	});

	omniFields.products = [...buyableStateItems, ...coldStateItems].reduce((acc, item) => {
		let itemTrackString = acc ? ',' : '';
		itemTrackString += getItemTrackString(item, isBasketItem);
		return acc + itemTrackString;
	}, '');

	const pincode = get(cartData, ['cartSummary', 'pincode']);
	const serviceableVars = getServiceableVars(defaultCartData);
	const oosItemCount = get(defaultCartData, ['coldStateItems', 'length']) || 0;
	const cartValue = get(cartData, ['cartSummary', 'price']) || 0;
	pincode && (omniFields.eVar52 = pincode);
	omniFields.prop60 = pincode ? 'pincode_present' : 'pincode_absent';
	omniFields.prop58 = serviceableVars.prop58;
	omnievents.push(`event109=${serviceableVars.event109}`);
	omnievents.push(`event113=${serviceableVars.event113}`);
	omnievents.push(`event116=${oosItemCount}`);
	omnievents.push(`event97=${cartValue}`);
	omnievents.push(`event95=${serviceableVars.event95}`);
	omnievents.push(`event96=${serviceableVars.event96}`);
	omnievents.push(`event91=${serviceableVars.event91}`);

	if (isBasketItem) {
		omnievents.push('event158=1');
	}

	omniFields.heir1 = pageName;

	const numberOfProducts =
		get(defaultCartData, ['buyableStateItems', 'length']) + get(defaultCartData, ['coldStateItems', 'length']) || 0;
	omnievents.push(`event108=${numberOfProducts}`);

	const otracker = get(location, ['query', 'otracker']);
	otracker && (omniFields.prop20 = otracker);

	omniFields.eVar70 = 'atln_v1';

	const fields = Object.assign(
		{},
		omnitureHelper.getBaseOmnitureFields(
			{
				pageName: omniContext.pageName,
				pageType: omniContext.pageType,
				location,
				userState: userState
			},
			isBasketItem
		),
		omniFields,
		{ events: omnievents.join(',') }
	);
	omnitureHelper.trackOmniturePageView(fields);
}

export function trackMoveToWishlist(item) {
	omnitureHelper.trackLink(
		{
			events: 'event21',
			products: getItemTrackString(item),
			prop3: pageType,
			prop4: pageName
		},
		'Cart: Move to Wishlist'
	);
}

export function trackMoveToSFL(item) {
	omnitureHelper.trackLink(
		{
			products: getItemTrackString(item),
			prop3: pageType,
			prop4: pageName,
			events: 'event227',
			prop24: 'Checkout_saveForLater_click'
		},
		'Cart: Move to Save For Later'
	);
}

export function trackMoveSFLToCart(item) {
	const { category, productId, vertical } = item;
	const itemString = `${category};${productId};;;;eVar22=${category}|eVar19=${vertical}|eVar18=SaveForLater`;
	omnitureHelper.trackLink(
		{
			products: itemString,
			prop3: pageType,
			prop4: pageName,
			events: 'event228',
			prop24: 'Checkout_MoveToCart_click'
		},
		'Cart: Move Save For Later to Cart'
	);
}

export function trackPincodeChange(cartDetails) {
	const serviceableVars = getServiceableVars(cartDetails);

	omnitureHelper.trackLink(
		{
			events: `event15,event109=${serviceableVars.event109},event113=${serviceableVars.event113}`,
			eVar52: get(cartDetails, ['cartSummary', 'pincode']),
			prop3: pageType,
			prop4: pageName,
			prop58: serviceableVars.prop58
		},
		'Cart: Pincode Change'
	);
}

export function trackRemoveCartItem(item, isBasketItem = false) {
	const pageName = isBasketItem ? basketPageName : pageName;
	const pageType = isBasketItem ? basketPageType : pageType;
	const sProducts = getItemTrackString(item, isBasketItem);

	const omnitureFields = {
		events: 'scRemove',
		prop24: 'Checkout_removefromCart_click',
		products: sProducts,
		pagename: pageName,
		prop3: pageType,
		prop4: pageName
	};
	if (!item.serviceAble || item.cartItemState === 'COLD_STATE') {
		omnitureFields.events += ',event112';
	}
	const eventName = isBasketItem ? 'Basket' : 'Cart';
	omnitureHelper.trackLink(omnitureFields, `${eventName}: Deletion`);
}

export function trackRemoveChildItem(item, childItemListingId) {
	const childItem = lookupChildItem(item, childItemListingId);

	if (!childItem) return;

	const omnitureFields = {
		events: 'event267',
		products: getItemTrackString(childItem, false, true),
		prop3: pageType,
		prop4: pageName
	};

	omnitureHelper.trackLink(omnitureFields, 'Cart: Child item deletion');
}

export function trackRemoveSFLItem(item) {
	const omnitureFields = {
		prop24: 'Checkout_removefromSavedList_click',
		products: getItemTrackString(item),
		pagename: pageName,
		prop3: pageType,
		prop4: pageName
	};
	omnitureHelper.trackLink(omnitureFields, 'Cart: SFL Deletion');
}
export function trackUndoRemove(isBasketItem = false) {
	const pageName = isBasketItem ? basketPageName : pageName;
	const pageType = isBasketItem ? basketPageType : pageType;
	const omnitureFields = {
		prop24: 'Checkout_UndoRemovefromCart',
		pagename: pageName,
		prop3: pageType,
		prop4: pageName
	};
	const eventName = isBasketItem ? 'Basket' : 'Cart';
	omnitureHelper.trackLink(omnitureFields, `${eventName}: Undo Remove`);
}

export function trackQuantityChange(item, isAdding = true, isBasketItem = false) {
	const pageName = isBasketItem ? basketPageName : pageName;
	const pageType = isBasketItem ? basketPageType : pageType;
	const omnitureFields = {
		events: 'event99',
		products: getItemTrackString(item, isBasketItem),
		pagename: pageName,
		prop3: pageType,
		prop4: pageName
	};
	const eventName = isBasketItem ? 'Basket' : 'Cart';
	omnitureHelper.trackLink(omnitureFields, `${eventName}: Quantity Change`);
}
export function trackBasketItemQuantityChange(item, isAdding = true) {
	const pageName = basketPageName;
	const pageType = basketPageType;
	const events = `evevent99, event157, ${isAdding ? 'scAdd' : 'scRemove'}`;
	const omnitureFields = {
		events: events,
		products: getItemTrackString(item, true),
		pagename: pageName,
		prop3: pageType,
		prop4: pageName
	};
	const eventName = 'Basket';
	omnitureHelper.trackLink(omnitureFields, eventName);
}

export function trackReCheckAvailability(item) {
	const omnitureFields = {
		events: 'event175',
		products: getItemTrackString(item),
		prop3: pageType,
		prop4: pageName
	};

	if (item.reservationDetails) {
		omnitureFields.products += `|evar94=${item.reservationDetails.saleId}_${item.reservationDetails.asmDisplayState}`;
	}

	omnitureHelper.trackLink(omnitureFields, 'ASM recheck availability');
}

export function trackASMCartExpiry(item) {
	const omnitureFields = {
		events: 'event174',
		products: getItemTrackString(item),
		prop3: pageType,
		prop4: pageName
	};

	if (item.reservationDetails) {
		omnitureFields.products += `|evar94=${item.reservationDetails.saleId}_${item.reservationDetails.asmDisplayState}`;
	}

	omnitureHelper.trackLink(omnitureFields, 'ASM timed out');
}
